import { Injectable } from '@angular/core';
import { ApiClient } from '../api.client';

import { Booking } from '../../models/booking';
import { BookingItem } from '../../models/booking-item';
import { BookingItemNamePipe } from '../pipes/booking-item-name.pipe';
import { ProductCategory } from '../../models/product-category';
import { hexToRgbLuma } from '../helpers/hexToRgbLuma';
import {
  DeliverableService,
  DeliveriesForDeliverable
} from '../deliveries/docket-delivery-list/deliverable-service.interface';
import { Observable } from 'rxjs';

@Injectable()
export class BookingService implements DeliverableService {
  apiUrl: string;

  constructor(
    private http: ApiClient,
    private bookingItemNamePipe: BookingItemNamePipe
  ) {
    this.apiUrl = this.http.API_URL;
  }

  getAll(include = []) {
    let qs = this.http.getWiths(include);
    return this.http.get<Booking[]>(`booking${qs}`);
  }

  getBooking(id: number, include = []) {
    let qs = this.http.getWiths(include);
    return this.http.get<Booking>(`booking/${id}${qs}`);
  }

  create(attributes) {
    return this.http.post<Booking>('booking', attributes);
  }

  update(id: number, booking: Partial<Booking>) {
    return this.http.put('booking/' + id, booking);
  }

  delete(id: number) {
    return this.http.delete('booking/' + id);
  }

  search(searchTerm) {
    return this.http.get(`booking/search/${searchTerm}`);
  }

  sendQuotation(
    booking: Booking,
    quotation: { note: string; deposit_amount?: number }
  ) {
    return this.http.post(`booking/${booking.id}/quotation`, quotation);
  }

  print(id) {
    return this.http.getResponse(`booking/${id}/print`);
  }

  bookingsToBookingListItems(bookings: Booking[]) {
    return bookings
      .flatMap((booking) => {
        return booking.items.map((item) => ({
          ...item,
          booking
        }));
      })
      .sort((a, b) => {
        return new Date(a.on_hire).getTime() - new Date(b.on_hire).getTime();
      });
  }

  bookingsToCalendarItems(bookings: Booking[]) {
    let calItems = [];
    bookings.forEach((booking) => {
      if (booking.items && booking.items.length) {
        calItems = this.bookingItemsToCalendarItems(booking.items, calItems);
      }
    });
    calItems.sort((a, b) => {
      return a.start.getTime() - b.start.getTime();
    });
    return calItems;
  }

  bookingItemsToCalendarItems(bookingItems: BookingItem[], calItems: any[]) {
    bookingItems.forEach((item) => {
      const rgb = this.getCategoryPrimaryColorRgb(item.category);
      let color = {
        primary: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`,
        secondary: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`
      };
      calItems.push({
        title: this.bookingItemNamePipe.transform(item),
        start: new Date(item.on_hire),
        end: new Date(item.off_hire),
        color: color,
        draggable: false,
        cssClass: rgb.luma > 120 ? 'light' : 'dark',
        resizable: {
          beforeStart: false,
          afterEnd: false
        },
        item: item
      });
    });
    return calItems;
  }

  getCategoryPrimaryColorRgb(category: ProductCategory) {
    const defaultColor = {
      r: 30,
      g: 144,
      b: 255,
      luma: 20
    };
    if (!category || !category.color) {
      return defaultColor;
    }
    let rgb = hexToRgbLuma(category.color);
    if (rgb) {
      return rgb;
    }

    return defaultColor;
  }

  getDeliveries(deliverableId: number): Observable<DeliveriesForDeliverable> {
    return this.http.get<DeliveriesForDeliverable>(
      `booking/${deliverableId}/deliveries`
    );
  }
}
